import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import GraphCanvas from './GraphCanvas';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Clock, Eye, LogIn, Lock, AlertTriangle, ExternalLink, Info } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * SnapshotViewer — public read-only page that renders a frozen Stack Map view.
 * Accessible at /snapshot/:slug. No login required. All interactive controls
 * (edit / integrate / add layer / dropdown menus) are hidden — only pan/zoom/hover.
 */
export default function SnapshotViewer({ slug, onGoToApp, onGoToLanding }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snap, setSnap] = useState(null);
  const graphRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    axios.get(`${BACKEND}/api/snapshots/${slug}`)
      .then(r => {
        setSnap(r.data);
        if (r.data?.node_id && r.data?.stack_data?.nodes) {
          const found = r.data.stack_data.nodes.find(n => n.id === r.data.node_id);
          if (found) setSelectedNode(found);
        }
      })
      .catch(err => {
        const detail = err.response?.data?.detail || 'Snapshot not found';
        setError(detail);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Once the graph mounts + data settles, apply the frozen layout mode
  useEffect(() => {
    if (!snap?.layout_mode || !snap?.stack_data) return;
    const t = setTimeout(() => {
      graphRef.current?.applyStoredLayout?.(snap.layout_mode);
    }, 900);
    return () => clearTimeout(t);
  }, [snap]);

  const createdAgo = useMemo(() => {
    if (!snap?.created_at) return '';
    try {
      const then = new Date(snap.created_at).getTime();
      const days = Math.max(1, Math.floor((Date.now() - then) / 86400000));
      return days === 1 ? '1 day ago' : `${days} days ago`;
    } catch { return ''; }
  }, [snap]);

  const expiresIn = useMemo(() => {
    if (!snap?.expires_at) return null;
    try {
      const t = new Date(snap.expires_at).getTime();
      const days = Math.round((t - Date.now()) / 86400000);
      if (days < 0) return 'expired';
      if (days === 0) return 'expires today';
      if (days === 1) return 'expires tomorrow';
      return `expires in ${days} days`;
    } catch { return null; }
  }, [snap]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
        <div className="max-w-md text-center px-6">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center mb-4">
            <AlertTriangle className="h-7 w-7 text-amber-600 dark:text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Snapshot unavailable</h1>
          <p className="text-sm text-muted-foreground mb-6">{error}</p>
          <div className="flex gap-2 justify-center">
            <Button onClick={onGoToLanding} variant="outline">Back to LodgiFlow</Button>
            <Button onClick={onGoToApp}><LogIn className="h-4 w-4 mr-2" />Sign in</Button>
          </div>
        </div>
      </div>
    );
  }

  const propLabel = snap.property_name || (snap.property_id === 'overall' ? 'Overall Map' : snap.property_id);

  return (
    <div className="h-screen w-screen flex flex-col bg-background" data-testid="snapshot-viewer">
      {/* Snapshot banner */}
      <div className="shrink-0 px-4 md:px-6 py-2.5 border-b bg-gradient-to-r from-indigo-50 via-slate-50 to-emerald-50 dark:from-indigo-950/30 dark:via-slate-900 dark:to-emerald-950/30 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <Lock className="h-4 w-4 text-indigo-600" />
          <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-400 uppercase tracking-wider">Snapshot View</span>
        </div>
        <div className="flex-1 min-w-[200px] flex items-baseline gap-2 truncate">
          <span className="text-sm font-semibold truncate" data-testid="snapshot-name">{snap.name || 'Untitled snapshot'}</span>
          <span className="text-xs text-muted-foreground truncate hidden md:inline">— {propLabel}{snap.node_name ? ` · ${snap.node_name}` : ''}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {createdAgo}</span>
          <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {snap.view_count || 0} views</span>
          {expiresIn && <Badge variant="outline" className="text-[9px]">{expiresIn}</Badge>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onGoToLanding} data-testid="snapshot-back">
            <ExternalLink className="h-3 w-3 mr-1" /> LodgiFlow
          </Button>
          <Button size="sm" className="h-7 text-xs" onClick={onGoToApp} data-testid="snapshot-signin">
            <LogIn className="h-3 w-3 mr-1" /> Sign in to interact
          </Button>
        </div>
      </div>

      {/* Read-only info bar */}
      <div className="shrink-0 px-4 md:px-6 py-1.5 border-b bg-muted/30 flex items-center gap-2 text-[10px] text-muted-foreground">
        <Info className="h-3 w-3 shrink-0" />
        <span>This is a frozen read-only view {createdAgo ? `captured ${createdAgo}` : ''}. Signing in lets you explore your own live stack.</span>
      </div>

      {/* Graph */}
      <div className="flex-1 relative overflow-hidden">
        {snap.stack_data ? (
          <GraphCanvas
            ref={graphRef}
            data={snap.stack_data}
            focusedCategory={snap.focused_category || null}
            selectedNode={selectedNode}
            onNodeSelect={setSelectedNode}
            searchQuery=""
            showUsedOnly={snap.filters?.show_used_only || false}
            hideUnconnected={snap.filters?.hide_unconnected || false}
            selectedProperty={snap.property_id}
            editMode={false}
            editSourceNode={null}
            compareMode={false}
            compareData={null}
            onLayoutChange={() => {}}
          />
        ) : (
          <div className="p-6 text-center text-sm text-muted-foreground">No graph data in this snapshot.</div>
        )}
      </div>

      {/* Footer watermark */}
      <div className="shrink-0 border-t bg-background px-4 py-1.5 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>Powered by <button onClick={onGoToLanding} className="text-primary hover:underline">LodgiFlow</button></span>
        <span>{snap.created_by_email ? `Shared by ${snap.created_by_email}` : ''}</span>
      </div>
    </div>
  );
}
